import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Alert,
  FormControl,
  FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import DialogTransition from 'src/components/DialogTransition';
import { useDispatch, useSelector } from 'react-redux';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { loadPendingPaymentIncomeCFDIsForClient } from 'src/store/actions/cfdiActions';
import { LOAD_PENDING_PAYMENT_INCOME_CFDI } from 'src/store/constants/CFDI';
import {
  reconcileSantanderPaymentAction,
  RECONCILE_SANTANDER_PAYMENT
} from 'src/store/actions/santanderClabeActions';
import {
  loadParameter,
  LOAD_PARAMETER,
  CLEAR_PARAMETER
} from 'src/store/actions/parameterActions';
import { Parameter } from 'src/utils/constants/parameters';
import { selectIndividualParameter } from 'src/store/selectors/parameterSelector';
import Loading from 'src/components/Loading';
import {
  getTotalReconciledAmount,
  getRemainingAmount,
  checkAmountExceeds
} from 'src/utils/payments';
import makeInitialState from './ReconcilePaymentModal/initialState';
import validationSchema from './ReconcilePaymentModal/schema';
import useStyles from './styles';

const ReconcilePaymentModal = ({ open, onClose, paymentData, reportId }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const resetRef = useRef();
  const dispatch = useDispatch();
  const [cfdiList, setCfdiList] = useState([]);
  const toleranceAmount = Number(useSelector(selectIndividualParameter)) || 0;
  const isLoadingCFDIs = useSelector(
    isLoadingSelector([LOAD_PENDING_PAYMENT_INCOME_CFDI.action])
  );
  const isLoadingReconcile = useSelector(
    isLoadingSelector([RECONCILE_SANTANDER_PAYMENT.action])
  );
  const isLoadingParameter = useSelector(
    isLoadingSelector([LOAD_PARAMETER.action])
  );

  const unreconciledAmt = paymentData.unreconciledAmount || 0;
  const effectiveMax = unreconciledAmt + toleranceAmount;

  const getCfdiDisplayName = cfdi => {
    if (!cfdi) return '';
    const contractFolio = cfdi.contract?.folio ?? '';
    return `${t('COMMON.CONTRACT')} ${contractFolio} - ${t('COMMON.SERIE')} ${cfdi.series} - ${cfdi.folio} - $${formatAmountWithDecimals(cfdi.amountIvaIncluded)}`;
  };

  const getAvailableCFDIs = selectedInvoices =>
    cfdiList.filter(cfdi => !selectedInvoices.some(inv => inv?.id === cfdi.id));

  const handleAddInvoice = (setFieldValue, invoices) => {
    setFieldValue('invoices', [...invoices, null]);
  };

  const handleRemoveInvoice = (index, setFieldValue, invoices) => {
    setFieldValue('invoices', invoices.filter((_, i) => i !== index));
  };

  const handleFormSubmit = values => {
    let totalAmount = 0;
    const invoices = values.invoices.reduce((acc, invoice) => {
      if (!invoice?.amountToPay) return acc;
      const amountToSend = Math.min(invoice.amountToPay, unreconciledAmt - totalAmount);
      if (amountToSend > 0) {
        acc.push({ id: invoice.id, amount: amountToSend });
        totalAmount += amountToSend;
      }
      return acc;
    }, []);

    dispatch(
      reconcileSantanderPaymentAction(paymentData.id, { invoices }, onClose, reportId)
    );
  };

  useEffect(() => {
    if (open && paymentData?.client?.id) {
      dispatch(
        loadPendingPaymentIncomeCFDIsForClient(paymentData.client.id, setCfdiList)
      );
      dispatch(loadParameter(Parameter.PAYMENT_TOLERANCE_AMOUNT));
    }
    return () => {
      resetRef.current?.();
      setCfdiList([]);
      dispatch({ type: CLEAR_PARAMETER });
    };
  }, [resetRef, dispatch, open, paymentData?.client?.id]);

  if (!open) return null;

  return (
    <Dialog
      disableEscapeKeyDown
      classes={{ paper: classes.dialogRoot }}
      open={open}
      keepMounted
      TransitionComponent={DialogTransition}
    >
      <DialogTitle className={classes.header}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            position: 'relative'
          }}
        >
          <Typography
            className={classes.header}
            color="primary"
            variant="h2"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {t('SANTANDER_REPORTS.RECONCILE_PAYMENT')}
          </Typography>
          <IconButton
            aria-label="Close"
            color="inherit"
            onClick={onClose}
            className={classes.closeIcon}
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isLoadingReconcile || isLoadingParameter ? (
          <Box
            sx={{
              width: '30rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px'
            }}
          >
            <Loading />
          </Box>
        ) : (
          <Formik
            initialValues={makeInitialState()}
            validationSchema={validationSchema(t)}
            onSubmit={handleFormSubmit}
          >
            {({ errors, handleSubmit, touched, values, resetForm, setFieldValue }) => {
              resetRef.current = resetForm;

              const invoices = values.invoices?.length ? values.invoices : [null];
              const invoicesErrors = errors.invoices || [];
              const availableCFDIs = getAvailableCFDIs(invoices);
              const rawInvoicesTotal = invoices.reduce(
                (sum, inv) => sum + (inv?.amountToPay || 0),
                0
              );
              const totalReconciledAmount = getTotalReconciledAmount(invoices, unreconciledAmt);
              const exceedsAmount = checkAmountExceeds(invoices, effectiveMax);
              const isAtCapacity = rawInvoicesTotal >= effectiveMax;

              return (
                <Form onSubmit={handleSubmit} sx={{ width: '30rem' }}>
                  {exceedsAmount && (
                    <Alert
                      severity="warning"
                      className={classes.warningAlert}
                      sx={{ width: '30rem' }}
                    >
                      {t('SANTANDER_REPORTS.AMOUNT_EXCEEDS_UNRECONCILED_AMOUNT')}
                    </Alert>
                  )}

                  <FormControl fullWidth sx={{ marginTop: 2 }}>
                    {invoices.map((invoice, index) => (
                      <Box
                        key={invoice?.id || `invoice-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexDirection: 'column',
                          height: '3.5rem',
                          marginTop: index === 0 ? '0px' : '16px'
                        }}
                      >
                        <Box
                          sx={{
                            width: '30rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Autocomplete
                            sx={{ flex: '1 0 auto', minWidth: 0 }}
                            options={availableCFDIs}
                            getOptionLabel={getCfdiDisplayName}
                            value={invoice || null}
                            onChange={(_, newValue) => {
                              const updated = [...invoices];
                              if (newValue) {
                                const remaining = getRemainingAmount(invoices, effectiveMax, index);
                                updated[index] = {
                                  ...newValue,
                                  amountToPay: Math.min(newValue.amountIvaIncluded, remaining)
                                };
                              } else {
                                updated[index] = null;
                              }
                              setFieldValue('invoices', updated);
                            }}
                            loading={isLoadingCFDIs}
                            loadingText={t('COMMON.LOADING')}
                            disabled={isLoadingCFDIs || isAtCapacity}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label={
                                  isLoadingCFDIs
                                    ? t('COMMON.LOADING')
                                    : t('SANTANDER_REPORTS.PENDING_PAYMENT_INVOICE')
                                }
                                variant="outlined"
                                placeholder={t('COMMON.SEARCH')}
                                error={Boolean(touched.invoices && invoicesErrors[index])}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                <Typography variant="body1">
                                  {getCfdiDisplayName(option)}
                                </Typography>
                              </Box>
                            )}
                            noOptionsText={t('SANTANDER_REPORTS.NO_INVOICES_FOUND')}
                          />

                          <IconButton
                            sx={{ width: '32px', height: '32px', flex: '0 0 32px', padding: '4px' }}
                            aria-label="delete"
                            disabled={invoices.length === 1}
                            onClick={() => handleRemoveInvoice(index, setFieldValue, invoices)}
                          >
                            <DeleteIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </Box>

                        <FormHelperText error>
                          {touched.invoices && invoicesErrors[index]
                            ? t(invoicesErrors[index])
                            : ' '}
                        </FormHelperText>
                      </Box>
                    ))}
                  </FormControl>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
                    <Button
                      type="button"
                      color="primary"
                      variant="text"
                      disabled={isLoadingCFDIs || isAtCapacity || availableCFDIs.length === 0}
                      onClick={() => handleAddInvoice(setFieldValue, invoices)}
                      startIcon={<AddIcon />}
                    >
                      {t('COMMON.ADD')}
                    </Button>
                  </Box>

                  {invoices.some(inv => inv?.amountToPay) && (
                    <Box className={classes.selectedInvoicesBox} sx={{ marginTop: 2 }}>
                      <Box className={classes.summaryBox}>
                        <Typography variant="body1" marginTop={2}>
                          <strong>
                            {t('SANTANDER_REPORTS.AMOUNT_TO_RECONCILE', {
                              amount: formatAmountWithDecimals(totalReconciledAmount)
                            })}
                          </strong>
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      margin: '1rem auto'
                    }}
                  >
                    <Button
                      onClick={onClose}
                      color="primary"
                      variant="outlined"
                      sx={{ minWidth: '45%' }}
                      disabled={isLoadingReconcile}
                    >
                      {t('COMMON.CANCEL')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: '45%' }}
                      disabled={isLoadingReconcile}
                    >
                      {isLoadingReconcile ? t('COMMON.LOADING') : t('SANTANDER_REPORTS.RECONCILE')}
                    </Button>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

ReconcilePaymentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  paymentData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    amount: PropTypes.number.isRequired,
    unreconciledAmount: PropTypes.number.isRequired,
    client: PropTypes.shape({
      id: PropTypes.number.isRequired,
      fullname: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  reportId: PropTypes.string.isRequired
};

export default ReconcilePaymentModal;
